import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { config } from '../config';
import { api } from '@/shared/api';
import { homeFor } from '@/shared/home';
import { frontendKeyHeader } from '@/shared/frontendKey';
import type { AuthState, LoginCredentials, RegisterData, AuthContextType, User, VerifyEmailData, ForgotPasswordData, ResetPasswordData } from '../types';

const AuthContext = createContext<AuthContextType | null>(null);

/*
 * These calls go through the app's own client rather than uikit's `useApi`,
 * because uikit's throws `HTTP ${status}: ${statusText}` and never reads the
 * body. Every refusal the API takes trouble to explain — "Invalid email or
 * password", "Your firm's Naumene account is suspended", a personal email
 * address on sign-up — arrived at the user as "HTTP 400: Bad Request", which
 * reads like the server is broken and tells them nothing to do about it.
 * `@/shared/api` throws an ApiError carrying the server's own message.
 */
type AuthResponse = { token?: string; user?: User; message?: string };

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      const token = localStorage.getItem(config.auth.storage.token);
      const userData = localStorage.getItem(config.auth.storage.user);

      if (token && userData) {
        try {
          const user = JSON.parse(userData);
          setAuthState({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          localStorage.removeItem(config.auth.storage.token);
          localStorage.removeItem(config.auth.storage.user);
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    // Initialize on mount
    initializeAuth();

    // Listen for localStorage changes (from UserContext profile updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === config.auth.storage.user && e.newValue) {
        try {
          const updatedUser = JSON.parse(e.newValue);
          setAuthState(prev => ({
            ...prev,
            user: updatedUser
          }));
        } catch (error) {
          console.error('Error parsing updated user data:', error);
        }
      }
    };

    // Also listen for custom events for same-tab updates
    const handleCustomUserUpdate = (e: CustomEvent) => {
      if (e.detail?.user) {
        setAuthState(prev => ({
          ...prev,
          user: e.detail.user
        }));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userUpdated', handleCustomUserUpdate as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleCustomUserUpdate as EventListener);
    };
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> => {
    try {
      // Don't set isLoading: true to prevent unnecessary re-renders
      // The LoginPage handles its own loading state with isSubmitting
      const response = await api.post<AuthResponse>('/api/auth/login', credentials);

      if (response.token && response.user && response.message === "Login successful") {
        const { user, token } = response;

        localStorage.setItem(config.auth.storage.token, token);
        localStorage.setItem(config.auth.storage.user, JSON.stringify(user));

        setAuthState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });

        /* Pathname AND search: somebody sent to sign in from
         * /auth/connect?request=… must come back to that request, not to a
         * bare page that then says the request is missing. */
        const from = (location.state as any)?.from as { pathname?: string; search?: string } | undefined;
        const intendedPath = from?.pathname
          ? `${from.pathname}${from.search ?? ''}`
          : config.auth.redirects.afterLogin;
        navigate(intendedPath, { replace: true });

        return { success: true };
      } else {
        // Don't change state on failed login to prevent re-renders
        return { success: false, error: response.message || 'Login failed' };
      }
    } catch (error: any) {
      // Don't change state on login error to prevent re-renders
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, [navigate, location]);

  const register = useCallback(async (data: RegisterData): Promise<{ success: boolean; error?: string }> => {
    try {
      /* Signing up creates the firm and signs its Owner straight in. */
      const response = await api.post<AuthResponse>('/api/auth/register', data);
      if (response.token && response.user) {
        localStorage.setItem(config.auth.storage.token, response.token);
        localStorage.setItem(config.auth.storage.user, JSON.stringify(response.user));
        setAuthState({ user: response.user, token: response.token, isAuthenticated: true, isLoading: false });
        navigate(homeFor(response.user), { replace: true });
        return { success: true };
      }
      return { success: false, error: response.message || 'Could not create your firm.' };
    } catch (error: any) {
      return { success: false, error: error?.response?.data?.message || error?.data?.message || error?.message || 'Network error occurred' };
    }
  }, [navigate]);

  const loginWithToken = useCallback(async (linkToken: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<AuthResponse>('/api/auth/token-login', { token: linkToken });
      if (response.token && response.user && response.message === 'Login successful') {
        const { user, token } = response;
        localStorage.setItem(config.auth.storage.token, token);
        localStorage.setItem(config.auth.storage.user, JSON.stringify(user));
        setAuthState({ user, token, isAuthenticated: true, isLoading: false });
        /* Straight to their own home — or to choosing a password, for a
         * member who has not set one yet. */
        navigate(homeFor(user), { replace: true });
        return { success: true };
      }
      return { success: false, error: response.message || 'That link did not work.' };
    } catch (error: any) {
      return { success: false, error: error?.response?.data?.message || error?.data?.message || error?.message || 'That link did not work.' };
    }
  }, [navigate]);

  const logout = useCallback((redirectTo: string = config.auth.redirects.afterLogout) => {
    /* Tell the server, so a console sign-out is audited. Fire and forget:
     * signing out must never wait on the network or fail because of it. */
    const token = localStorage.getItem(config.auth.storage.token);
    if (token) {
      void fetch(`${import.meta.env.VITE_API_URL ?? 'http://localhost:3000'}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...frontendKeyHeader() },
        keepalive: true,
      }).catch(() => undefined);
    }
    localStorage.removeItem(config.auth.storage.token);
    localStorage.removeItem(config.auth.storage.user);

    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });

    navigate(redirectTo, { replace: true });
  }, [navigate]);

  const verifyEmail = useCallback(async (data: VerifyEmailData): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<AuthResponse>('/api/auth/verify-email', data);

      if (response.message === "Email verified successfully") {
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Email verification failed' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, []);

  const resendVerification = useCallback(async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<AuthResponse>('/api/auth/resend-verification', { email });

      if (response.message === "Verification email sent successfully") {
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Failed to send verification email' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, []);

  const forgotPassword = useCallback(async (data: ForgotPasswordData): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<AuthResponse>('/api/auth/forgot-password', data);

      if (response.message && response.message.includes("password reset link has been sent")) {
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Failed to send reset email' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, []);

  const resetPassword = useCallback(async (data: ResetPasswordData): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<AuthResponse>('/api/auth/reset-password', data);

      if (response.message === "Password reset successfully") {
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Password reset failed' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, []);


  const value: AuthContextType = {
    ...authState,
    login,
    loginWithToken,
    register,
    logout,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};